import {ErrorHandler, Injectable} from '@angular/core'
import {browserApiErrorsIntegration, captureException, getCurrentScope, init as initSentry} from '@sentry/angular-ivy'
import {HttpErrorResponse} from '@angular/common/http'
import {environment} from '../../../environments/environment'
import {ConfigService} from '../config/config.service'
import {Logger} from '../logging/logger'
import {differenceInMinutes} from 'date-fns'
import {LocalStorageService} from '../storage/local-storage.service'

export const LAST_CHUNK_FAILED_ERROR_KEY = 'bgo.last-chunk-error-date'

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandler implements ErrorHandler {
  static sentryEnabled = environment.sentry.enabled
  static loadSentry() {
    if (this.sentryEnabled) {
      initSentry({
        dsn: 'https://9935b9777a1c46ab963f39e82a6b7f44@o58092.ingest.sentry.io/5353411',
        integrations: [
          // browserApiErrorsIntegration has to be configured to disable XMLHttpRequest wrapping, as we are going to handle
          // http module exceptions manually in Angular's ErrorHandler and we don't want it to capture the same error twice.
          browserApiErrorsIntegration({XMLHttpRequest: false}),
          // browserTracingIntegration(), // disabled for bundle size reason, re-enable if we want performance tracing
        ],
        // capture a fraction of traces for perf monitoring, as suggested by sentry
        tracesSampleRate: 0.1,
      })
    }
  }

  constructor(
    private configService: ConfigService,
    private localStorageService: LocalStorageService,
  ) {
    if (GlobalErrorHandler.sentryEnabled) {
      const config = this.configService.config
      getCurrentScope().addEventProcessor(event => {
        event.environment = config.stage
        event.release = config.version
        return event
      })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extractError(error: any) {
    // Try to unwrap zone.js error.
    // https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
    if (error && error.ngOriginalError) {
      error = error.ngOriginalError
    }

    // We can handle messages and Error objects directly.
    if (typeof error === 'string' || error instanceof Error) {
      return error
    }

    // If it's http module error, extract as much information from it as we can.
    if (error instanceof HttpErrorResponse) {
      // The `error` property of http exception can be either an `Error` object, which we can use directly...
      if (error.error instanceof Error) {
        return error.error
      }

      // ... or an`ErrorEvent`, which can provide us with the message but no stack...
      if (error.error instanceof ErrorEvent) {
        return error.error.message
      }

      // ...or the request body itself, which we can use as a message instead.
      if (typeof error.error === 'string') {
        return `Server returned code ${error.status} with body "${error.error}"`
      }

      // If we don't have any detailed information, fallback to the request message itself.
      return error.message
    }

    // Skip if there's no error, and let user decide what to do with it.
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleError(error: any) {
    if (this.isChunkLoadingFailedErrorHandled(error)) {
      return
    }

    const extractedError = this.extractError(error) || 'Handled unknown error'
    Logger.error(extractedError)

    if (GlobalErrorHandler.sentryEnabled) {
      // Capture handled exception and send it to Sentry.
      captureException(extractedError)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isChunkLoadingFailedErrorHandled(error: any): boolean {
    // handle special case where app was remotely updated
    // lazily loaded chunks referenced by currently old running version no longer exist
    // https://medium.com/fieldcircle/error-loading-chunk-xx-failed-with-angular-lazy-loaded-modules-6c5b1b6f8b8d
    const chunkFailedMessage = /Loading chunk [\d]+ failed/
    if (error?.message && chunkFailedMessage.test(error.message)) {
      // if an error occurred less than 5 minutes ago, it may be another issue and we want to avoid infinite reload
      if (this.isLastChunkErrorDateFarEnough()) {
        Logger.debug('Caught `Loading chunk failed` due to a chunk that no longer exists, reloading the page...')
        this.storeLastChunkErrorDate()
        window?.location?.reload()
        return true
      }
    }

    return false
  }

  private isLastChunkErrorDateFarEnough(): boolean {
    const lastError = this.lastChunkErrorDate()
    if (lastError) {
      return differenceInMinutes(new Date(), lastError) > 5
    } else {
      return true
    }
  }

  private lastChunkErrorDate(): Date | undefined {
    const item = this.localStorageService.getItem(LAST_CHUNK_FAILED_ERROR_KEY)
    if (item) {
      return new Date(item)
    } else {
      return undefined
    }
  }

  private storeLastChunkErrorDate() {
    this.localStorageService.setItem(LAST_CHUNK_FAILED_ERROR_KEY, new Date().toISOString())
  }
}
